import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCashierStatus } from '../services/cashierService';
import { Schemas } from '../types/api-types';
import { useUserPermissionAndMenu } from './UserPermissionAndMenuProvider';

type CashBook = Schemas['CashBookResponse'];
interface CashierContextType {
    isSignedIn: boolean;
    loading: boolean;
    cashBook: CashBook | null;
    isCashier: boolean;
    setCashBook: (account: CashBook | null) => void;
    setIsSignedIn(signedIn: boolean): void;
}

const CashierContext = createContext<CashierContextType | undefined>(undefined);

export const CashierProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isSignedIn, setIsSignedIn] = useState(false);
    const [isCashier, setIsCashier] = useState(false);
    const [cashBook, setCashBook] = useState<CashBook | null>(null);
    const [loading, setLoading] = useState(true);
    const { userPermissions } = useUserPermissionAndMenu();

    useEffect(() => {
        // Check if user has any transaction-related permissions
        const hasTransactionPermission = userPermissions?.some(perm =>
            perm.permission?.toLowerCase().includes('transaction') ||
            perm.permission?.toLowerCase().includes('receipt') ||
            perm.permission?.toLowerCase().includes('payment') ||
            perm.permission?.toLowerCase().includes('withdraw') ||
            perm.permission?.toLowerCase().includes('journal')
        );

        if (hasTransactionPermission) {
            setLoading(true);
            setIsCashier(true);
            fetchCashierStatus();
        }
        else {
            setIsSignedIn(false);
            setCashBook(null);
            setLoading(false);
        }
    }, [userPermissions]);

    const fetchCashierStatus = async () => {
        try {
            const cashierStatus = await getCashierStatus();
            setIsSignedIn(cashierStatus.signed_in);
            setCashBook(cashierStatus.cash_book || null);
        } catch (error: any) {
            setIsSignedIn(false);
            setCashBook(null);
        } finally {
            setLoading(false);
        }
    };

    return (
        <CashierContext.Provider value={
            {
                isSignedIn,
                loading,
                cashBook,
                isCashier,
                setCashBook,
                setIsSignedIn
            }
        }>
            {children}
        </CashierContext.Provider>
    );
};

export const useCashier = () => {
    const context = useContext(CashierContext);
    if (!context) {
        throw new Error('useCashier must be used within an CashierProvider');
    }
    return context;
};
